import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ContentTypeSwitcher } from '../components/ui';

/**
 * How to Use Page - Educational resource for AI newcomers
 * Explains prompts, AI agents, and MCP servers with examples
 */
function HowToUse() {
  const [activeType, setActiveType] = useState('prompt');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-900 dark:to-purple-900">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Master AI with Boop 👻
            </h1>
            <p className="text-xl text-indigo-100 dark:text-indigo-200 mb-8">
              Your complete guide to prompts, AI agents, and MCP servers.
              <br />
              From beginner to pro in minutes.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/prompts/create"
                className="px-8 py-3 bg-white text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 transition-colors"
              >
                Start Creating
              </Link>
              <Link
                to="/dashboard"
                className="px-8 py-3 bg-indigo-500 text-white rounded-lg font-semibold hover:bg-indigo-400 transition-colors"
              >
                Browse Library
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Content Type Switcher */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <ContentTypeSwitcher
            activeType={activeType}
            onChange={setActiveType}
            types={['prompt', 'agent', 'mcp_server']}
            variant="tabs"
          />
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          {activeType === 'prompt' && <PromptsGuide />}
          {activeType === 'agent' && <AgentsGuide />}
          {activeType === 'mcp_server' && <MCPServersGuide />}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-100 dark:bg-gray-800 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Join the Community?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              Create your free account and start sharing your AI creations today.
            </p>
            <Link
              to="/signup"
              className="inline-block px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-colors"
            >
              Sign Up Free
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Prompts Guide Section
 */
function PromptsGuide() {
  return (
    <div className="space-y-12">
      {/* What are Prompts */}
      <Section
        icon="💬"
        title="What are AI Prompts?"
        description="AI prompts are instructions you give to AI models like ChatGPT, Claude, or GPT-4. Think of them as recipes - the better the recipe, the better the result."
      >
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
            Example: Basic vs. Great Prompt
          </h4>
          <div className="space-y-4">
            <div>
              <div className="text-sm text-red-600 dark:text-red-400 font-medium mb-2">
                ❌ Basic Prompt (Poor Results)
              </div>
              <code className="block bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700 text-sm">
                Write a blog post
              </code>
            </div>
            <div>
              <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-2">
                ✅ Great Prompt (Excellent Results)
              </div>
              <code className="block bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700 text-sm">
                Write a 1000-word blog post about sustainable living for urban millennials.
                Include 5 practical tips, use a conversational tone, and add relevant statistics.
                End with a compelling call-to-action.
              </code>
            </div>
          </div>
        </div>
      </Section>

      {/* How to Write Great Prompts */}
      <Section
        icon="✍️"
        title="How to Write Great Prompts"
        description="Follow these proven principles to get amazing results from any AI model."
      >
        <div className="grid md:grid-cols-2 gap-6">
          <TipCard
            number="1"
            title="Be Specific"
            description="Include details about tone, length, format, and audience. The more specific, the better the output."
            example="Write a friendly 500-word email explaining blockchain to a 10-year-old."
          />
          <TipCard
            number="2"
            title="Give Context"
            description="Provide background information and explain what you're trying to achieve."
            example="I'm a startup founder pitching to investors. Help me write a compelling one-liner for my AI app."
          />
          <TipCard
            number="3"
            title="Use Examples"
            description="Show the AI what you want by providing examples or templates."
            example="Write 3 subject lines like: '🚀 You won't believe this new feature' but for a B2B SaaS product."
          />
          <TipCard
            number="4"
            title="Define the Role"
            description="Tell the AI to act as an expert in a specific field."
            example="Act as a senior marketing strategist. Analyze my website's value proposition and suggest improvements."
          />
        </div>
      </Section>

      {/* Prompt Categories */}
      <Section
        icon="📂"
        title="Popular Prompt Categories"
        description="Explore different types of prompts on Boop and find what works for your needs."
      >
        <div className="grid md:grid-cols-3 gap-4">
          <CategoryCard
            icon="💼"
            title="Business & Marketing"
            examples={['Sales emails', 'Social media posts', 'Product descriptions', 'Ad copy']}
          />
          <CategoryCard
            icon="💻"
            title="Coding & Tech"
            examples={['Code debugging', 'Algorithm explanations', 'Code reviews', 'Documentation']}
          />
          <CategoryCard
            icon="✨"
            title="Creative Writing"
            examples={['Story ideas', 'Character development', 'Dialogue', 'Poetry']}
          />
          <CategoryCard
            icon="📚"
            title="Education & Learning"
            examples={['Study guides', 'Explanations', 'Quiz questions', 'Summaries']}
          />
          <CategoryCard
            icon="🎨"
            title="Design & Art"
            examples={['Color palettes', 'Design critiques', 'Art descriptions', 'Mood boards']}
          />
          <CategoryCard
            icon="🔬"
            title="Research & Analysis"
            examples={['Data analysis', 'Research synthesis', 'Literature reviews', 'Reports']}
          />
        </div>
      </Section>

      {/* Getting Started */}
      <Section
        icon="🚀"
        title="Get Started with Prompts"
        description="Join thousands of creators sharing and discovering amazing AI prompts."
      >
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-8">
          <div className="space-y-4">
            <Step number="1" text="Browse our library of prompts" link="/dashboard?type=prompt" />
            <Step number="2" text="Try prompts with your favorite AI" />
            <Step number="3" text="Rate prompts with Boops (⭐) to help others" />
            <Step number="4" text="Create and share your own prompts" link="/prompts/create" />
            <Step number="5" text="Level up from Beginner (B) to Professional (P)" />
          </div>
        </div>
      </Section>
    </div>
  );
}

/**
 * AI Agents Guide Section
 */
function AgentsGuide() {
  return (
    <div className="space-y-12">
      {/* What are AI Agents */}
      <Section
        icon="🤖"
        title="What are AI Agents?"
        description="AI Agents are pre-configured AI assistants with specific personalities, knowledge, and capabilities. They're like hiring an expert for a specific task."
      >
        <div className="bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
            Agent vs. Regular Prompt
          </h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-2">
                Regular Prompt
              </div>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>• Single use instruction</li>
                <li>• No memory or context</li>
                <li>• Same output every time</li>
                <li>• Limited capabilities</li>
              </ul>
            </div>
            <div>
              <div className="text-sm text-pink-600 dark:text-pink-400 font-medium mb-2">
                AI Agent
              </div>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>• Persistent personality & knowledge</li>
                <li>• Remembers conversation context</li>
                <li>• Adapts to your needs</li>
                <li>• Can use tools & APIs</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Agent Components */}
      <Section
        icon="⚙️"
        title="How AI Agents Work"
        description="Agents combine multiple components to create intelligent, specialized assistants."
      >
        <div className="grid md:grid-cols-2 gap-6">
          <ComponentCard
            icon="🎭"
            title="System Prompt"
            description="Defines the agent's personality, expertise, and behavior. This is the agent's 'DNA'."
            example="You are a senior software architect specializing in React and Node.js. Provide detailed, production-ready code."
          />
          <ComponentCard
            icon="🧰"
            title="Tools & Capabilities"
            description="Special functions the agent can use like web search, calculations, or API calls."
            tools={['web_search', 'code_execution', 'image_generation', 'file_analysis']}
          />
          <ComponentCard
            icon="🧠"
            title="Memory Types"
            description="How the agent remembers information across conversations."
            tools={['Ephemeral (single chat)', 'Short-term (session)', 'Long-term (persistent)']}
          />
          <ComponentCard
            icon="📏"
            title="Context Window"
            description="How much conversation history the agent can remember at once."
            example="8K tokens = ~6,000 words | 32K tokens = ~24,000 words"
          />
        </div>
      </Section>

      {/* Popular Agent Types */}
      <Section
        icon="🌟"
        title="Popular Agent Types"
        description="Discover what agents people are building and using on Boop."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <AgentTypeCard
            emoji="👨‍💼"
            title="Business Analyst Agent"
            description="Analyzes data, creates reports, and provides strategic insights."
            use="Upload spreadsheets, get insights, make data-driven decisions."
          />
          <AgentTypeCard
            emoji="🎨"
            title="Creative Director Agent"
            description="Brainstorms ideas, provides feedback, and guides creative projects."
            use="Get design critiques, generate campaign ideas, refine branding."
          />
          <AgentTypeCard
            emoji="💻"
            title="Code Review Agent"
            description="Reviews code, suggests improvements, and catches bugs."
            use="Submit code snippets, get detailed reviews, learn best practices."
          />
          <AgentTypeCard
            emoji="📚"
            title="Research Assistant Agent"
            description="Searches the web, synthesizes information, and creates summaries."
            use="Ask research questions, get cited answers, build knowledge bases."
          />
        </div>
      </Section>

      {/* Getting Started with Agents */}
      <Section
        icon="🚀"
        title="Get Started with AI Agents"
        description="Create your first AI agent or explore what others have built."
      >
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-xl p-8">
          <div className="space-y-4">
            <Step number="1" text="Explore existing agents for inspiration" link="/dashboard?type=agent" />
            <Step number="2" text="Try agents to see how they work" />
            <Step number="3" text="Fork (clone) agents and customize them" />
            <Step number="4" text="Create your own agent from scratch" link="/agents/create" />
            <Step number="5" text="Share with the community and collect boops" />
          </div>
        </div>
      </Section>
    </div>
  );
}

/**
 * MCP Servers Guide Section
 */
function MCPServersGuide() {
  return (
    <div className="space-y-12">
      {/* What are MCP Servers */}
      <Section
        icon="🔌"
        title="What are MCP Servers?"
        description="MCP (Model Context Protocol) servers let AI assistants interact with external tools, APIs, and services. They're like plugins that give AI superpowers."
      >
        <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
            What MCP Servers Enable
          </h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-2">
                Without MCP
              </div>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>❌ Can't access your files</li>
                <li>❌ Can't search the web</li>
                <li>❌ Can't use databases</li>
                <li>❌ Can't call APIs</li>
              </ul>
            </div>
            <div>
              <div className="text-sm text-cyan-600 dark:text-cyan-400 font-medium mb-2">
                With MCP Servers
              </div>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li>✅ Read and write files</li>
                <li>✅ Search and fetch data</li>
                <li>✅ Query databases</li>
                <li>✅ Integrate any API</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* How MCP Works */}
      <Section
        icon="🔧"
        title="How MCP Servers Work"
        description="MCP creates a standardized way for AI clients to communicate with external tools."
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="space-y-6">
            <FlowStep
              number="1"
              title="You connect an MCP server to your AI client"
              description="Like installing a plugin in Claude Desktop or Cline"
            />
            <FlowStep
              number="2"
              title="The AI discovers available tools"
              description="The server tells the AI what it can do (search files, query database, etc.)"
            />
            <FlowStep
              number="3"
              title="You chat naturally with the AI"
              description="'Find all TypeScript files in my project'"
            />
            <FlowStep
              number="4"
              title="The AI uses the tools automatically"
              description="Calls the MCP server's file search function and returns results"
            />
          </div>
        </div>
      </Section>

      {/* Popular MCP Servers */}
      <Section
        icon="⚡"
        title="Popular MCP Servers"
        description="Essential servers that extend your AI's capabilities."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <MCPServerCard
            icon="📁"
            title="File System Server"
            description="Read, write, and search files on your computer."
            compatible={['Claude Desktop', 'Cline', 'Continue']}
            useCase="Code analysis, documentation generation, project organization"
          />
          <MCPServerCard
            icon="🌐"
            title="Web Search Server"
            description="Search the internet and fetch web pages."
            compatible={['Claude Desktop', 'Cline']}
            useCase="Research, fact-checking, finding recent information"
          />
          <MCPServerCard
            icon="💾"
            title="Database Server"
            description="Query SQL databases (PostgreSQL, MySQL, SQLite)."
            compatible={['Claude Desktop', 'Cline', 'Continue']}
            useCase="Data analysis, report generation, schema exploration"
          />
          <MCPServerCard
            icon="🐙"
            title="GitHub Server"
            description="Interact with GitHub repos, issues, and PRs."
            compatible={['Claude Desktop', 'Cline']}
            useCase="Code reviews, issue management, repository analysis"
          />
        </div>
      </Section>

      {/* Getting Started with MCP */}
      <Section
        icon="🚀"
        title="Get Started with MCP Servers"
        description="Set up your first MCP server and supercharge your AI workflow."
      >
        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl p-8">
          <div className="space-y-4">
            <Step number="1" text="Browse the MCP server library" link="/dashboard?type=mcp_server" />
            <Step number="2" text="Find a server that matches your needs" />
            <Step number="3" text="Follow the installation instructions" />
            <Step number="4" text="Configure it in your AI client (Claude Desktop, Cline, etc.)" />
            <Step number="5" text="Start using powerful new capabilities!" />
          </div>
          <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-cyan-200 dark:border-cyan-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-cyan-600 dark:text-cyan-400">💡 Pro Tip:</strong> Many servers work with
              Claude Desktop (official), Cline (VS Code extension), and Continue (IDE extension). Check compatibility
              before installing.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}

/**
 * Reusable Components
 */

function Section({ icon, title, description, children }) {
  return (
    <div>
      <div className="flex items-start gap-4 mb-6">
        <div className="text-4xl">{icon}</div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {title}
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            {description}
          </p>
        </div>
      </div>
      {children}
    </div>
  );
}

function TipCard({ number, title, description, example }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
          {number}
        </div>
        <h3 className="font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {description}
      </p>
      {example && (
        <div className="bg-gray-50 dark:bg-gray-900 rounded p-3 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-700 dark:text-gray-300 font-mono">
            {example}
          </p>
        </div>
      )}
    </div>
  );
}

function CategoryCard({ icon, title, examples }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-shadow">
      <div className="text-3xl mb-2">{icon}</div>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <ul className="space-y-1">
        {examples.map((example, i) => (
          <li key={i} className="text-sm text-gray-600 dark:text-gray-400">
            • {example}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ComponentCard({ icon, title, description, example, tools }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {description}
      </p>
      {example && (
        <div className="bg-pink-50 dark:bg-pink-900/20 rounded p-3 border border-pink-200 dark:border-pink-800">
          <p className="text-xs text-gray-700 dark:text-gray-300">
            {example}
          </p>
        </div>
      )}
      {tools && (
        <div className="space-y-1 mt-3">
          {tools.map((tool, i) => (
            <div key={i} className="text-xs bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 px-2 py-1 rounded inline-block mr-2">
              {tool}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AgentTypeCard({ emoji, title, description, use }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow">
      <div className="text-4xl mb-3">{emoji}</div>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {description}
      </p>
      <div className="bg-pink-50 dark:bg-pink-900/20 rounded p-3 border border-pink-200 dark:border-pink-800">
        <p className="text-xs text-gray-700 dark:text-gray-300">
          <strong>Use it for:</strong> {use}
        </p>
      </div>
    </div>
  );
}

function MCPServerCard({ icon, title, description, compatible, useCase }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow">
      <div className="text-4xl mb-3">{icon}</div>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {description}
      </p>
      <div className="space-y-2">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-500 font-medium mb-1">
            Compatible with:
          </div>
          <div className="flex flex-wrap gap-1">
            {compatible.map((client, i) => (
              <span key={i} className="text-xs bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 px-2 py-1 rounded">
                {client}
              </span>
            ))}
          </div>
        </div>
        <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded p-2 border border-cyan-200 dark:border-cyan-800">
          <p className="text-xs text-gray-700 dark:text-gray-300">
            <strong>Use case:</strong> {useCase}
          </p>
        </div>
      </div>
    </div>
  );
}

function Step({ number, text, link }) {
  const content = (
    <div className="flex items-center gap-4">
      <div className="w-8 h-8 bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
        {number}
      </div>
      <p className="text-gray-700 dark:text-gray-300">
        {text}
      </p>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block hover:opacity-80 transition-opacity">
        {content}
      </Link>
    );
  }

  return content;
}

function FlowStep({ number, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 bg-cyan-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
        {number}
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
          {title}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {description}
        </p>
      </div>
    </div>
  );
}

export default HowToUse;
